import { css, styled } from "styled-components";

import {
    colorCSS,
    colorCSSforSVG,
    fontSizes,
    generateFieldsStyles,
    gradientExists,
    gradients,
    h2,
    isBackgroundFieldArray,
    minWidth,
    radius
} from "../../styles";
import { redundantCssFromKebab } from "../../styles/redundant-css";

const sharedContainerStyles = css`
    ${redundantCssFromKebab("display", "flex")}
    ${redundantCssFromKebab("flex-direction", "column")}
    ${redundantCssFromKebab("height", "100%")}
    ${redundantCssFromKebab("margin", "0")}
    ${redundantCssFromKebab("padding", "2rem")}

    @media ${minWidth.lg} {
        ${redundantCssFromKebab("padding", "2.5rem 2rem")}
    }
`;

export const ProductFact = styled.div<{ background: unknown }>`
    ${sharedContainerStyles}

    ${({ background }) =>
        isBackgroundFieldArray(background) && generateFieldsStyles(background)}
    
    h3 {
        ${h2}
        ${redundantCssFromKebab("margin", "0")}
    }

    @media ${minWidth.lg} {
        ${redundantCssFromKebab("width", "33.3333%")}
    }
`;

export const ProductFactLink = styled.div`
    ${redundantCssFromKebab("margin-top", "auto")}
    ${redundantCssFromKebab("padding-top", "1rem")}
    @media ${minWidth.lg} {
        ${redundantCssFromKebab("padding-top", "2rem")}
    }
`;

export const Quote = styled.blockquote`
    ${redundantCssFromKebab("font-size", `${fontSizes.lg}`)}
    ${redundantCssFromKebab("margin", "0")}

    > div p:last-child {
        ${redundantCssFromKebab("margin-bottom", "0")}
    }
`;

export const QuoteCaption = styled.figcaption`
    ${redundantCssFromKebab("margin-top", "auto")}
    ${redundantCssFromKebab("padding-top", "1rem")}

    &:before {
        ${redundantCssFromKebab("background-color", "currentColor")}
        ${redundantCssFromKebab("content", '""')}
        ${redundantCssFromKebab("display", "block")}
        ${redundantCssFromKebab("height", "1px")}
        ${redundantCssFromKebab("margin-bottom", "1rem")}
        ${redundantCssFromKebab("max-width", "9rem")}
    }

    p {
        ${redundantCssFromKebab("margin", "0")}
    }
    @media ${minWidth.lg} {
        ${redundantCssFromKebab("padding-top", "3.25rem")}
    }
`;

export const QuoteFigure = styled.figure<{ background: unknown }>`
    ${sharedContainerStyles}

    ${({ background }) => {
        const gradient = getGradientFromBackground(background);

        if (!gradient) return;

        return `
            background-image: url("data:image/svg+xml,%3Csvg fill='none' height='272' width='773' xmlns='http://www.w3.org/2000/svg' %3E%3Cpath d='M351.53 186.369c0-53.88-28.777-83.707-84.412-83.707h-.96c11.511-29.826 48.921-45.22 97.842-48.107V-33c-126.619 6.735-196.643 77.934-196.643 185.694 0 85.631 40.288 119.306 94.964 119.306 54.677 0 89.209-32.713 89.209-85.631Zm-203.357 0c0-53.88-28.777-83.707-84.413-83.707h-.959c11.51-29.826 48.921-45.22 97.842-48.107V-33C34.024-26.265-36 44.934-36 152.694-36 238.325 4.288 272 58.964 272s89.209-32.713 89.209-85.631Z' fill='url(%23b)' opacity='.15' /%3E%3Cdefs%3E%3ClinearGradient id='b' x1='290.667' y1='-33' x2='141.42' y2='307.405' gradientUnits='userSpaceOnUse' %3E%3Cstop stop-color='${colorCSSforSVG(
                gradient.colors[1]
            )}' /%3E%3Cstop stop-color='${colorCSSforSVG(
            gradient.colors[0]
        )}' offset='1' /%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E");
            background-repeat: no-repeat;
        `;
    }}
    
    @media ${minWidth.lg} {
        width: 66.6666%;
    }
`;

export const QuoteImageLink = styled.div`
    ${redundantCssFromKebab("margin-top", "auto")}

    a,
    img {
        ${redundantCssFromKebab("display", "block")}
        ${redundantCssFromKebab("margin-top", "1rem")}
    }

    img {
        ${redundantCssFromKebab("max-height", "2.25rem")}
        ${redundantCssFromKebab("max-width", "100%")}
        ${redundantCssFromKebab("width", "auto")}
    }
    @media ${minWidth.lg} {
        ${redundantCssFromKebab("align-items", "baseline")}
        ${redundantCssFromKebab("display", "flex")}
        ${redundantCssFromKebab("justify-content", "space-between")}

        a,
        img {
            ${redundantCssFromKebab("margin-top", "1.5rem")}
        }
    }
`;

export const SocialProof = styled.div`
    ${redundantCssFromKebab("background-color", `${colorCSS("N0")}`)}
    ${redundantCssFromKebab("border-radius", `${radius.small}`)}
    box-shadow: 0px 3px 5px ${colorCSS("N900", 0.2)},
        0px 0px 1px ${colorCSS("N900", 0.31)};
    ${redundantCssFromKebab("display", "flex")}
    ${redundantCssFromKebab("flex-direction", "column")}
    ${redundantCssFromKebab("font-size", `${fontSizes.sm}`)}
    ${redundantCssFromKebab("height", "100%")}
    ${redundantCssFromKebab("justify-content", "space-between")}
    ${redundantCssFromKebab("overflow", "hidden")}
    ${redundantCssFromKebab("text-align", "left")}
    @media ${minWidth.lg} {
        ${redundantCssFromKebab("flex-direction", "row")}
    }
`;

export const ShadowWrapper = styled.div`
    ${redundantCssFromKebab("height", "100%")}
    ${redundantCssFromKebab("padding", "1px 0.5rem 0.5rem")}
`;

//get first gradient from background field
function getGradientFromBackground(backgrounds: unknown) {
    if (!isBackgroundFieldArray(backgrounds)) return;

    const backgroundsWithGradients = backgrounds.filter((background) =>
        background.properties.filter((property) => property.image.gradient)
    );

    const gradient =
        backgroundsWithGradients[0]?.properties[0]?.image.gradient.value;

    if (!gradient || !gradientExists(gradient)) return;

    return gradients[gradient];
}
